import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, PlugZap, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_ICON = {
  mex_diocese: (
    <div className="w-10 h-10 rounded-md bg-teal-100 border border-teal-200 flex items-center justify-center font-display font-bold text-teal-800">M</div>
  ),
  maximo_uon: (
    <div className="w-10 h-10 rounded-md bg-indigo-100 border border-indigo-200 flex items-center justify-center font-display font-bold text-indigo-800">Mx</div>
  ),
};

export default function SettingsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/integrations");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (item) => {
    setEditing(item);
    // Start blank so users don't accidentally overwrite with masked strings
    const blank = {};
    for (const f of item.fields) blank[f.key] = "";
    setForm(blank);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/integrations/save", { provider: editing.provider, config: form });
      toast.success(`${editing.name} credentials saved`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Could not save credentials");
    } finally { setSaving(false); }
  };

  const disconnect = async (p) => {
    try {
      await api.post(`/integrations/${p}/disconnect`);
      toast.success("Disconnected");
      await load();
    } catch { toast.error("Failed to disconnect"); }
  };

  return (
    <AppLayout title="Client Integrations" subtitle="Connect client-owned systems (MEX, Maximo) into the GBE Hub via the governance framework">
      <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-md bg-emerald-100 border border-emerald-200 flex items-center justify-center font-display font-bold text-emerald-800">📄</div>
        <div className="flex-1">
          <div className="font-medium text-slate-900">Data-mapping template</div>
          <div className="text-xs text-slate-500">Pre-populated .xlsx with Aroflo + Xero fields discovered live from your active connections, plus starter rows for MYOB/MEX/Maximo/SharePoint. Fill it in and share back for wiring.</div>
        </div>
        <a
          data-testid="download-mapping-template"
          href={`${process.env.REACT_APP_BACKEND_URL}/api/tools/data-mapping-template`}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-gbe hover:bg-gbe-hover text-white text-sm font-medium"
        >
          Download .xlsx
        </a>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-40 bg-white border border-slate-200 rounded-lg shimmer" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div
              key={it.provider}
              data-testid={TID.integrationCard(it.provider)}
              className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col"
            >
              <div className="flex items-start justify-between">
                {PROVIDER_ICON[it.provider] || (
                  <div className="w-10 h-10 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center font-display font-bold text-slate-700">
                    {it.name?.[0] || "?"}
                  </div>
                )}
                {it.connected ? (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 font-medium">
                    <CheckCircle2 className="w-3 h-3" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 font-medium">
                    <XCircle className="w-3 h-3" /> Not connected
                  </span>
                )}
              </div>
              <div className="mt-4">
                <div className="font-display text-lg font-semibold text-slate-900">{it.name}</div>
                <div className="text-xs text-slate-500">{it.category}</div>
                {it.client && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 uppercase tracking-wider font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                    Client: {it.client}
                  </div>
                )}
              </div>
              <p className="mt-3 text-sm text-slate-600">{it.help}</p>
              {it.connected && (
                <div className="mt-3 rounded-md bg-slate-50 border border-slate-100 p-2 text-[11px] text-slate-500 font-mono truncate">
                  {Object.entries(it.masked_config).map(([k, v]) => `${k}: ${v || "—"}`).join("  ·  ")}
                </div>
              )}
              <div className="mt-auto pt-4 flex gap-2">
                <Button
                  data-testid={TID.integrationConnect(it.provider)}
                  onClick={() => openEdit(it)}
                  className="bg-gbe hover:bg-gbe-hover text-white h-9"
                >
                  <PlugZap className="w-4 h-4 mr-1.5" />
                  {it.connected ? "Update" : "Connect"}
                </Button>
                {it.connected && (
                  <Button
                    data-testid={TID.integrationDisconnect(it.provider)}
                    onClick={() => disconnect(it.provider)}
                    variant="outline"
                    className="h-9"
                  >
                    Disconnect
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Connect {editing.name}</DialogTitle>
                <DialogDescription>{editing.help}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {editing.fields.map((f) => (
                  <div key={f.key}>
                    <Label htmlFor={f.key}>{f.label}</Label>
                    <Input
                      id={f.key}
                      type={f.type}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="mt-1.5 font-mono text-sm"
                    />
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button
                  data-testid={TID.integrationSave(editing.provider)}
                  onClick={save}
                  disabled={saving}
                  className="bg-gbe hover:bg-gbe-hover text-white"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save credentials"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
